import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import ProductCard from '../components/ProductCard';
import { REFINE_BY_TAGS } from '../lib/constants';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tagSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const search = searchParams.get('search') || '';
  const productType = searchParams.get('product_type') || '';
  const tag = searchParams.get('tag') || '';
  const sort = searchParams.get('sort') || 'name';

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (productType) params.set('product_type', productType);
    if (tag) params.set('tag', tag);
    params.set('sort', sort);
    params.set('page', 1);
    // Single-page catalog — return up to 500 products so customers never see
    // pagination for any current filter. (Catalog has ~26 products; bumping
    // here gives ~20x headroom before we'd need real infinite scroll.)
    params.set('limit', 500);
    axios.get(`${API}/products?${params.toString()}`).then(r => {
      setProducts(r.data.products); setTotal(r.data.total); setPages(r.data.pages);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [search, productType, tag, sort]);

  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  };
  const clearFilters = () => setSearchParams({});
  const hasActiveFilters = search || productType || tag;
  const getPageTitle = () => {
    if (productType === 'peptide') return 'Peptides';
    if (productType === 'equipment') return 'Equipment';
    return 'All Products';
  };

  const FilterContent = ({ includeSort = false }) => (
    <div className="space-y-6">
      <div>
        <h4 className="font-heading font-semibold text-sm mb-3 text-foreground">Product Type</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant={!productType ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', '')} data-testid="filter-product-type-all">All</Badge>
          <Badge variant={productType === 'peptide' ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', 'peptide')} data-testid="filter-product-type-peptides">Peptides</Badge>
          <Badge variant={productType === 'equipment' ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', 'equipment')} data-testid="filter-product-type-equipment">Equipment</Badge>
        </div>
      </div>
      <Separator />
      <div>
        <h4 className="font-heading font-semibold text-sm mb-3 text-foreground">Refine by</h4>
        <div className="flex flex-col gap-2">
          {REFINE_BY_TAGS.map(t => (
            <Badge
              key={t}
              variant={tag === t ? "default" : "outline"}
              className="cursor-pointer w-fit font-normal text-xs"
              onClick={() => updateParam('tag', tag === t ? '' : t)}
              data-testid={`filter-tag-${tagSlug(t)}`}
            >
              {t}
            </Badge>
          ))}
        </div>
      </div>
      {includeSort && (
        <>
          <Separator />
          <div>
            <h4 className="font-heading font-semibold text-sm mb-3 text-foreground">Sort By</h4>
            <Select value={sort} onValueChange={(v) => updateParam('sort', v)}>
              <SelectTrigger data-testid="sort-select-mobile"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="popular">Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      {hasActiveFilters && (<><Separator /><Button variant="ghost" size="sm" onClick={clearFilters} className="w-full" data-testid="clear-filters"><X className="h-4 w-4 mr-1" /> Clear All Filters</Button></>)}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="catalog-page">
      <SEO
        title={
          tag ? `${tag} — Research Peptides` :
          productType === 'peptide' ? 'Research-Grade Peptides' :
          productType === 'equipment' ? 'Lab Equipment & Supplies' :
          'Catalog — Peptides & Equipment'
        }
        description={
          tag ? `Shop ${tag} research peptides at Wilvada — HPLC-verified ≥99% purity, COA included with every order.` :
          'Browse research-grade peptides (Retatrutide GLP3-RT, BPC-157, GHK-Cu, Tesamorelin, MOTS-c, NAD+, Semax & more) plus lab equipment from Wilvada Peptide Solutions.'
        }
      />
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,30%)] mb-2 font-body">Catalog</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-foreground font-heading">{getPageTitle()}</h1>
        <p className="mt-2 text-base text-muted-foreground font-body">{total} {total === 1 ? 'product' : 'products'} available</p>
      </div>

      {/* Search + Sort + mobile filter trigger */}
      <div className="flex gap-3 mb-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search products..." value={search} onChange={(e) => updateParam('search', e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <div className="hidden md:flex items-center gap-2 ml-auto">
          <span className="text-xs font-body text-muted-foreground whitespace-nowrap">Sort By:</span>
          <div className="w-56">
            <Select value={sort} onValueChange={(v) => updateParam('sort', v)}>
              <SelectTrigger data-testid="sort-select-desktop"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="popular">Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetTrigger asChild><Button variant="outline" size="icon" className="md:hidden" data-testid="mobile-filter-trigger"><SlidersHorizontal className="h-4 w-4" /></Button></SheetTrigger>
          <SheetContent side="left" className="w-80"><SheetHeader><SheetTitle className="font-heading">Filters</SheetTitle></SheetHeader><div className="mt-6"><FilterContent includeSort={true} /></div></SheetContent>
        </Sheet>
      </div>

      {/* Product Type + Refine by — below Search + Sort */}
      <div className="hidden md:block mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Product Type:</span>
          <Badge variant={!productType ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', '')} data-testid="filter-product-type-all">All</Badge>
          <Badge variant={productType === 'peptide' ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', 'peptide')} data-testid="filter-product-type-peptides">Peptides</Badge>
          <Badge variant={productType === 'equipment' ? "default" : "outline"} className="cursor-pointer" onClick={() => updateParam('product_type', 'equipment')} data-testid="filter-product-type-equipment">Equipment</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Refine by:</span>
          {REFINE_BY_TAGS.map(t => (
            <Badge
              key={t}
              variant={tag === t ? "default" : "outline"}
              className="cursor-pointer font-normal text-xs"
              onClick={() => updateParam('tag', tag === t ? '' : t)}
              data-testid={`filter-tag-${tagSlug(t)}`}
            >
              {t}
            </Badge>
          ))}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearFilters} data-testid="clear-filters-desktop">
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1">
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-4 md:hidden">
            {productType && (<Badge variant="secondary" className="gap-1">{productType === 'peptide' ? 'Peptides' : 'Equipment'}<X className="h-3 w-3 cursor-pointer" onClick={() => updateParam('product_type', '')} /></Badge>)}
            {tag && (<Badge variant="secondary" className="gap-1">{tag}<X className="h-3 w-3 cursor-pointer" onClick={() => updateParam('tag', '')} /></Badge>)}
            {search && (<Badge variant="secondary" className="gap-1">"{search}"<X className="h-3 w-3 cursor-pointer" onClick={() => updateParam('search', '')} /></Badge>)}
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => (<div key={i} className="bg-muted animate-pulse rounded-lg h-80" />))}</div>
        ) : products.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results"><p className="text-lg font-heading text-foreground mb-2">No products found</p><Button variant="outline" onClick={clearFilters}>Clear Filters</Button></div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">{products.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}</div>
          </>
        )}
      </div>
    </div>
  );
}
